// client/src/pages/AIscanner.js
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Navigate } from 'react-router-dom';

function AIscanner() {
  const { isSignedIn } = useAuth();
  
  // If user is signed in, redirect to dashboard
  if (isSignedIn) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return (
    <div className="AIscanner">
      {/* Hero Section */}
      <section className="bg-gray-100 py-10 mt-0 w-screen right-[50%] relative left-[50%] right-[50%] mx-[-50vw]">
        <div className="container max-w-5xl mx-auto px-6">
          <h1 className="text-3xl text-gray-900 md:text-5xl mr-5 font-bold leading-tight mb-4">
            Extract Complex Data With AI
          </h1>
          <p className="text-xl mb-1 mr-5">
            Get your product IDs, SNS codes, and more from images, then create QR codes in seconds.
          </p>
        </div>
        {/* <div className="container max-w-6xl mx-auto px-6 mb-4">
          <img src="/scanner.gif" alt="BoxyQR AI Scanner in action" 
               onError={(e) => {e.target.onerror = null; e.target.src = "/scanner-fallback.png";}} />
        </div> */}
      </section>

      {/* Get Started Section */}
      <div className="flex justify-center p-3">
        <div className="grid w-full max-w-2xl md:grid-cols-2 gap-4 mb-1 ">
          <Link to="/sign-up" className="flex bg-blue-700 justify-center border-2 border-blue-700 text-white hover:bg-blue-100 hover:text-blue-800 px-10 py-5 rounded-lg font-semibold text-center transition duration-200">
            Start Scanning Free
          </Link>
          
          <Link to="/benefits" className="flex bg-transparent justify-center border-2 text-blue-700 border-blue-700 hover:bg-blue-100 hover:text-indigo-800 px-10 py-5 rounded-lg font-semibold text-center transition duration-200">
          Benefits & Pricing
          </Link>
        </div>
      </div>
      <p className="flex mt-1 justify-center w-full text-sm opacity-80"></p>

      {/* AI Scanner Section */}
      <section className="py-16">
        <div className="container max-w-4xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-10 md:mb-0 md:pr-10">
              <h2 className="text-3xl font-bold mb-6">AI-Powered Data Extraction</h2>
              <p className="text-lg text-gray-600 mb-6">
                Take or upload photos of your products and tell our AI what to extract. It instantly pulls product IDs, serial numbers, device IDs, and more, saving you hours of manual data entry.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Smart recognition of various ID formats</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Bulk upload and analyze images</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Simple guided extraction with natural language</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Support for batch processing and automation</span>
                </li>
              </ul>
                            <Link 
                            to={`/sign-up`} 
                            className="mt-5 inline-block text-blue-600 hover:text-blue-800 text-m font-semibold"
                          >
                            It's Free, Try It →
                          </Link>
            </div>
            <div className="md:w-1/2">
              <div className="">
                <img src="/scanner.gif" alt="BoxyQR Data Extraction Example" className="rounded shadow-lg" 
                     onError={(e) => {e.target.onerror = null; e.target.src = "/scanner-fallback.png";}} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CSV Export Section */}
      <section className="py-16">
        <div className="container max-w-4xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2">
              <div className="">
                <img src="/AIexport.png" alt="BoxyQR CSV Export Feature" className="rounded" 
                     onError={(e) => {e.target.onerror = null; e.target.src = "/AIexport.png";}} />
              </div>
            </div>
            <div className="md:w-1/2 mb-10 md:mb-0 md:pl-10 md:pr-0">
              <h2 className="text-3xl font-bold mb-6">One-Click CSV Export</h2>
              <p className="text-lg text-gray-600 mb-6">
                After extracting data, export it to CSV with a single click. Then use our bulk upload tool to create trackable QR codes for every item in seconds.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Export all extracted data in standardized format</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Compatible with our bulk QR code generator</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Add custom fields and organize your data</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Process hundreds of entries in minutes</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Bulk QR Generation Section */}
      <section className="py-16 bg-gray-100 w-screen right-[50%] relative left-[50%] right-[50%] mx-[-50vw]">
        <div className="container max-w-4xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-10 md:mb-0 md:pr-10">
              <h2 className="text-3xl font-bold mb-6">Bulk QR Code Groups</h2>
              <p className="text-lg text-gray-600 mb-6">
                Turn your extracted data into dynamic QR codes in seconds. Just upload your CSV, set your destinations, and create trackable QR codes for your entire inventory at once.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Create hundreds of QR codes in one operation</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Bulk update URLs without reprinting</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Track scans for individual items</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Customize QR codes with your branding</span>
                </li>
              </ul>
              <div className="mt-5 pt-4">
                  <Link 
                    to={`/QRcreator`} 
                    className="inline-block text-blue-600 hover:text-blue-800 text-m font-semibold"
                  >
                    Learn More →
                  </Link>
                </div>
            </div>
            <div className="md:w-1/2">
              <div className="">
                <img src="/bulk.png" alt="BoxyQR Bulk QR Generation" className="rounded shadow-lg" 
                     onError={(e) => {e.target.onerror = null; e.target.src = "/custom.png";}} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic URLs Section */}
      <section className="py-16">
        <div className="container max-w-4xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Smart Data Extraction</h3>
              <p className="text-gray-600">Our AI recognizes product IDs, serial numbers, IMEI codes, and other identifiers with incredible accuracy.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Dynamic Destinations</h3>
              <p className="text-gray-600">Update QR code destinations anytime without reprinting - direct customers to the right place instantly.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Complete Analytics</h3>
              <p className="text-gray-600">Track every scan with location, device, and time data to understand your product engagement.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-16 bg-gray-100 w-screen right-[50%] relative left-[50%] right-[50%] mx-[-50vw]">
        <div className="container max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12">From Image to QR Code in 3 Steps</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-full text-white flex items-center justify-center text-3xl font-bold mx-auto mb-4">1</div>
              <h3 className="text-xl font-semibold mb-2">Scan Images</h3>
              <p className="text-gray-600">Upload photos and extract IDs</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-full text-white flex items-center justify-center text-3xl font-bold mx-auto mb-4">2</div>
              <h3 className="text-xl font-semibold mb-2">Export to CSV</h3>
              <p className="text-gray-600">Add URLs and prepare for bulk upload</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-full text-white flex items-center justify-center text-3xl font-bold mx-auto mb-4">3</div>
              <h3 className="text-xl font-semibold mb-2">Generate QR Codes</h3>
              <p className="text-gray-600">Create and track all your QR codes</p>
            </div>
          </div>
          <div className="flex justify-center mt-8 p-3">
            <div className="grid w-full max-w-2xl md:grid-cols-1 gap-4 mb-1">
              <Link to="/sign-up" className="flex bg-blue-700 justify-center border-2 border-blue-700 text-white hover:bg-blue-100 hover:text-blue-800 px-10 py-5 rounded-lg font-semibold text-center transition duration-200">
                Start Scanning Free
              </Link>
            </div>
          </div>
          <p className="mt-3 justify-center w-full text-center text-sm opacity-80">AI scanner • Bulk QR generation • Real-time analytics</p>
        </div>
      </section>

      {/* Feature Cards */}
      {/* <section className="py-16 bg-gray-50 w-screen right-[50%] relative left-[50%] right-[50%] mx-[-50vw]">
        <div className="container max-w-5xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12">Key Features</h2>
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-green-500">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold">AI Smart Scanner</h3>
              </div>
              <p className="text-gray-600 mb-4">Extract data like product IDs, serial numbers, and device IDs from images with our AI-powered recognition system.</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <svg className="h-4 w-4 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Auto-detect custom identifiers</span>
                </li>
                <li className="flex items-center">
                  <svg className="h-4 w-4 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Process hundreds of images at once</span>
                </li>
                <li className="flex items-center">
                  <svg className="h-4 w-4 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>One-click CSV export</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-indigo-500">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 015.656 0l4 4a4 4 0 01-5.656 5.656l-1.102-1.101" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold">Dynamic QR Management</h3>
              </div>
              <p className="text-gray-600 mb-4">Generate bulk QR codes from your extracted data and update destinations anytime without reprinting.</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <svg className="h-4 w-4 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Bulk QR code generation</span>
                </li>
                <li className="flex items-center">
                  <svg className="h-4 w-4 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Update URLs without reprinting</span>
                </li>
                <li className="flex items-center">
                  <svg className="h-4 w-4 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Detailed scan analytics</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-16 w-screen right-[50%] relative left-[50%] right-[50%] mx-[-50vw]">
        <div className="container max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to save hours of data entry?</h2>
          <p className="text-xl mb-8">Join other businesses using BoxyQR's AI scanner to streamline their workflow from data capture to QR code tracking.</p>
        </div>
        <div className="flex justify-center p-3">
          <div className="grid w-full max-w-2xl md:grid-cols-2 gap-4 mb-1">
            <Link to="/sign-up" className="flex justify-center bg-white text-blue-700 border-2 border-white hover:bg-blue-200 hover:text-blue-800 px-10 py-5 rounded-lg font-semibold transition text-center duration-200">
              Start Scanning Free
            </Link>
            
            <Link to="/benefits" className="flex bg-transparent justify-center border-2 text-white border-white hover:bg-blue-600 hover:text-white px-10 py-5 rounded-lg font-semibold text-center transition duration-200">
            Benefits & Pricing
            </Link>
          </div>
        </div>
        <p className="mt-3 justify-center w-full text-center text-sm opacity-80">AI scanner • Bulk QR generation • Real-time analytics</p>
      </section>
    </div>
  );
}

export default AIscanner;